/**
 * The week highlighter's arithmetic — ports of the generator's `resolveScheduleItems`,
 * `buildWeeks` and `itemsInWeek`.
 *
 * Kept as plain functions with no React in them, because the Timeline view in
 * Phase 5 needs the same schedule resolution and the two must never disagree
 * about when a room runs. That was the reason the generator shared it too.
 *
 * One deliberate difference from the generator, carried over from its own
 * comment: there is NO fallback reference schedule here. The generator falls
 * back to the hardcoded insurance dates when a workbook has no Schedule sheet,
 * and its own week control explicitly refuses to use that fallback, because
 * highlighting an arbitrary plan's rooms against the insurance dates would imply
 * a link that does not exist. In this application the data comes from the
 * database, where no such fallback is meaningful at all, so a blueprint with no
 * schedule rows simply has no week control.
 */
import type { RoomModel } from '@blueprint/shared';
import type { ScheduleRow } from '../../lib/blueprints';

export interface ScheduleItem {
  roomID: string;
  /** Blank means the row covers the whole room; otherwise it scopes to one pillar. */
  pillarName: string;
  person: string;
  name: string;
  start: string;
  end: string;
  who: string;
}

export interface Week {
  start: string;
  end: string;
}

const MS = 86400000;

/**
 * Accepts a real date, an Excel serial number, or a 'YYYY-MM-DD' string —
 * the generator's `parseAnyDate`, kept because a workbook-sourced cell may still
 * arrive in any of those forms.
 */
export function parseAnyDate(v: unknown): Date {
  if (v instanceof Date) return v;
  if (typeof v === 'number') return new Date(Date.UTC(1899, 11, 30) + v * MS);
  return new Date(String(v).slice(0, 10) + 'T00:00:00');
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function metaDate(v: unknown): string | null {
  if (v === null || v === undefined || v === '') return null;
  // A 'YYYY-MM-DD' string is already the answer. Going through parseAnyDate and
  // toISOString would read it as LOCAL midnight and print it in UTC, which in any
  // zone east of UTC moves every date back a day (SAST: 11 Sep becomes 10 Sep).
  // The API sends dates in exactly this form, so the round trip was never needed.
  if (typeof v === 'string') {
    const iso = /^(\d{4}-\d{2}-\d{2})/.exec(v.trim());
    if (iso) return iso[1] ?? null;
  }
  const d = parseAnyDate(v);
  return Number.isNaN(d.getTime()) ? null : isoDate(d);
}

/** Monday of the week containing `d`, in local time. Mon=0 … Sun=6. */
export function mondayOf(d: Date): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - dow);
  return x;
}

export function ymd(d: Date): string {
  return (
    d.getFullYear() +
    '-' +
    String(d.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(d.getDate()).padStart(2, '0')
  );
}

export function dayFmt(s: string): string {
  const d = new Date(s + 'T00:00:00');
  return d.getDate() + ' ' + d.toLocaleString('en-GB', { month: 'short' });
}

/** Schedule rows to dated items. Rows without both dates are dropped, as in the generator. */
export function resolveScheduleItems(rows: ScheduleRow[], rooms: RoomModel[]): ScheduleItem[] {
  const roomByID = new Map(rooms.map((r) => [String(r.RoomID), r]));

  return rows
    .map((r) => {
      const room = roomByID.get(String(r.RoomID));
      const pillar = r.PillarName === undefined || r.PillarName === null ? '' : String(r.PillarName).trim();
      const person = r.Person === undefined || r.Person === null ? '' : String(r.Person).trim().toUpperCase();
      const base = room ? `${r.RoomID} ${room.RoomName}` : String(r.RoomID);
      return {
        roomID: String(r.RoomID),
        pillarName: pillar,
        person,
        name: pillar ? `${base} — ${pillar}` : base,
        start: metaDate(r.StartDate),
        end: metaDate(r.EndDate),
        who: r.Lead ?? '',
      };
    })
    .filter((i): i is ScheduleItem => Boolean(i.start && i.end));
}

/** Every Mon–Fri week the schedule spans, so the slider steps linearly through time. */
export function buildWeeks(items: ScheduleItem[]): Week[] {
  if (!items.length) return [];

  const times: number[] = [];
  for (const i of items) {
    times.push(new Date(i.start + 'T00:00:00').getTime());
    times.push(new Date(i.end + 'T00:00:00').getTime());
  }

  let cur = mondayOf(new Date(Math.min(...times)));
  const lastMon = mondayOf(new Date(Math.max(...times)));
  const out: Week[] = [];
  while (cur.getTime() <= lastMon.getTime()) {
    const fri = new Date(cur);
    fri.setDate(fri.getDate() + 4);
    out.push({ start: ymd(cur), end: ymd(fri) });
    cur = new Date(cur);
    cur.setDate(cur.getDate() + 7);
  }
  return out;
}

/** Items whose span overlaps the given week at all. */
export function itemsInWeek(items: ScheduleItem[], wk: Week): ScheduleItem[] {
  const ws = new Date(wk.start + 'T00:00:00').getTime();
  const we = new Date(wk.end + 'T00:00:00').getTime();
  return items.filter((i) => {
    const s = new Date(i.start + 'T00:00:00').getTime();
    const e = new Date(i.end + 'T00:00:00').getTime();
    return s <= we && e >= ws;
  });
}

/**
 * The week containing today, clamped into the plan's own range — the generator's
 * default selection. Returns -1 when there are no weeks to pick from.
 */
export function defaultWeekIndex(weeks: Week[], today = new Date()): number {
  if (!weeks.length) return -1;
  const todayMon = ymd(mondayOf(today));
  const idx = weeks.findIndex((w) => w.start === todayMon);
  if (idx !== -1) return idx;
  // Before the plan begins, clamp to its first week; after it ends, to its last.
  const first = weeks[0];
  return first && todayMon < first.start ? 0 : weeks.length - 1;
}

/**
 * Indices (`__i`) of the deliverables inside the selected week.
 *
 * A deliverable is in scope if its room matches an active schedule item and —
 * where that item names a pillar — its pillar matches too. Returned as a Set so
 * the chip components can ask in constant time.
 */
export function highlightedIndices(
  deliverables: { RoomID: string; PillarName: string; __i?: number }[],
  active: ScheduleItem[],
): Set<number> {
  const out = new Set<number>();
  if (!active.length) return out;

  for (const d of deliverables) {
    const hit = active.some(
      (i) => String(d.RoomID) === i.roomID && (!i.pillarName || String(d.PillarName).trim() === i.pillarName),
    );
    if (hit && d.__i !== undefined) out.add(d.__i);
  }
  return out;
}
